/**
 * Live provider tracking API.
 *
 * Two rules govern the whole feature, both resolved by the backend:
 *  - opt-in per service type (`ServiceTemplate.allow_live_tracking`), and
 *  - active only while the service is `in_progress`.
 * `liveTrackingActive` already combines them — never recompute it from `status`.
 */

import { graphqlQuery } from '@/services/graphql'

// ============================================================================
// Types
// ============================================================================

/**
 * A provider's last-known position. Coordinates are exposed as numbers here; see
 * {@link fetchServiceLiveLocation} for why that coercion is not optional.
 */
export interface ServiceLiveLocation {
  latitude: number
  longitude: number
  headingDegrees: number | null
  accuracyMeters: number | null
  reportedAt: string
  /** Seconds since the position was reported. */
  ageSeconds: number
  /** Backend-owned freshness verdict (older than LIVE_TRACKING_STALE_AFTER_SECONDS). */
  isStale: boolean
  providerName: string | null
  serviceStatus: string | null
}

/**
 * Per-service tracking flags.
 *
 * Deliberately fetched by their own query rather than added to `ORDER_DETAILS_QUERY`: an
 * unknown field fails GraphQL validation for the *whole* document, so selecting these on
 * the order query would take the entire order details page down on any backend that has
 * not deployed PR #264 yet. Isolated here, a schema mismatch costs only the tracking card.
 */
export interface ServiceTrackingFlags {
  id: string
  status: string
  /** This service *type* allows tracking. */
  liveTrackingEnabled: boolean
  /** A tracking window is open right now. Gate every UI decision on this. */
  liveTrackingActive: boolean
}

// ============================================================================
// GraphQL Queries
// ============================================================================

export const SERVICE_LIVE_LOCATION_QUERY = `
query ServiceLiveLocation($serviceId: ID!) {
  serviceLiveLocation(serviceId: $serviceId) {
    latitude
    longitude
    headingDegrees
    accuracyMeters
    reportedAt
    ageSeconds
    isStale
    providerName
    serviceStatus
  }
}`

/**
 * Deliberately tiny: this is polled, and `ORDER_DETAILS_QUERY` is ~180 lines of
 * selection set that must not be.
 */
export const ORDER_TRACKING_FLAGS_QUERY = `
query OrderTrackingFlags($id: ID!) {
  order(id: $id) {
    id
    services {
      id
      status
      liveTrackingEnabled
      liveTrackingActive
    }
  }
}`

// ============================================================================
// Fetchers
// ============================================================================

/** `latitude`/`longitude` are GraphQL Decimals, so they arrive as strings. */
interface RawServiceLiveLocation extends Omit<ServiceLiveLocation, 'latitude' | 'longitude'> {
  latitude: string | number
  longitude: string | number
}

interface ServiceLiveLocationResponse {
  serviceLiveLocation: RawServiceLiveLocation | null
}

interface OrderTrackingFlagsResponse {
  order: { id: string; services: ServiceTrackingFlags[] } | null
}

function toNumber(value: string | number | null | undefined): number {
  return typeof value === 'number' ? value : parseFloat(String(value))
}

/**
 * Fetch the provider's last-known position for one service.
 *
 * Returns `null` for every ordinary "nothing to show" case — the tracking window is
 * closed, the provider has not reported yet, or the viewer is not permitted to see it.
 * These are not distinguished by the backend and must not be surfaced as errors.
 */
export async function fetchServiceLiveLocation(
  serviceId: string
): Promise<ServiceLiveLocation | null> {
  const response = await graphqlQuery<ServiceLiveLocationResponse>(
    SERVICE_LIVE_LOCATION_QUERY,
    { serviceId }
  )

  const raw = response.data?.serviceLiveLocation
  if (!raw) return null

  // Decimal → number. Handing a string to a map SDK yields a silent marker at 0,0.
  const latitude = toNumber(raw.latitude)
  const longitude = toNumber(raw.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null

  return { ...raw, latitude, longitude }
}

/** Fetch the tracking flags for every service on an order. */
export async function fetchOrderTrackingFlags(
  orderId: string
): Promise<ServiceTrackingFlags[]> {
  const response = await graphqlQuery<OrderTrackingFlagsResponse>(
    ORDER_TRACKING_FLAGS_QUERY,
    { id: orderId }
  )
  return response.data?.order?.services ?? []
}
